import { aiService } from "@/services/aiService";
import { getCurrentUserId } from "@/lib/supabase";
import { operatingLoopService } from "@/services/operatingLoopService";
import { creditsService } from "@/services/creditsService";
import { buildContextAwareSystemPromptCore } from "@/services/chatSystemPromptForThread";
import { aiAgentsService } from "@/services/aiAgentsService";
import { AGENTS } from "@/data/aiToolkit";
import { composeSkillInstructions, getSkillById } from "@/data/agentSkills";
import { BUILTIN_WORKER_PROFILES } from "@/data/workerProfiles";
import type { WorkflowNode } from "@/types/aiToolkit";
import type { NodeOutput, WorkerProfile, WorkflowRunState } from "@/types/workflowEngine";
import { buildBusContextText } from "./graphTraverser";
import { userAgentSkillsService } from "@/services/userAgentSkillsService";

async function resolveWorkerProfile(profileId?: string | null): Promise<Partial<WorkerProfile>> {
  if (profileId) {
    const { workerProfilesService } = await import("@/services/workerProfilesService");
    const p = await workerProfilesService.getById(profileId);
    if (p) return p;
  }
  return BUILTIN_WORKER_PROFILES[2] as WorkerProfile;
}

function parseStructuredOutput(content: string): { parsed?: unknown; confidence?: number; quality?: number } {
  const trimmed = content.trim();
  try {
    const jsonMatch = trimmed.match(/```json\s*([\s\S]*?)```/) || trimmed.match(/^(\{[\s\S]*\})$/);
    const jsonStr = jsonMatch ? jsonMatch[1] : trimmed;
    if (jsonStr.startsWith("{") || jsonStr.startsWith("[")) {
      const parsed = JSON.parse(jsonStr);
      const confidence = typeof parsed.confidence === "number" ? parsed.confidence : undefined;
      const quality = typeof parsed.quality === "number" ? parsed.quality : undefined;
      return { parsed, confidence, quality };
    }
  } catch {
    // not json
  }
  return {};
}

export async function executeAgentNode(
  node: WorkflowNode,
  state: WorkflowRunState,
  options?: { userContent?: string; agentIdOverride?: string; app?: string }
): Promise<NodeOutput> {
  const gate = await creditsService.assertPositiveBalanceForAi();
  if (!gate.ok) {
    return { raw: "", parsed: { error: "Credits depleted" } };
  }

  const agentId = options?.agentIdOverride ?? node.data.agentId;
  if (!agentId) {
    return { raw: "", parsed: { error: "No agent configured" } };
  }

  let agent = await aiAgentsService.getAgentById(agentId);
  if (!agent) {
    agent = AGENTS.find((a) => a.id === agentId) as typeof agent;
  }
  if (!agent) {
    return { raw: "", parsed: { error: `Agent not found: ${agentId}` } };
  }

  const profile = await resolveWorkerProfile(node.data.workerProfileId ?? agent.workerProfileId);
  const skillIds = [...(agent.attachedSkillIds ?? []), ...(node.data.skillIds ?? [])];
  let skillBlock = composeSkillInstructions(skillIds);

  for (const sid of skillIds) {
    if (!getSkillById(sid)) {
      const userSkill = await userAgentSkillsService.getById(sid);
      if (userSkill) skillBlock += `\n\n## SKILL: ${userSkill.name}\n${userSkill.instructions}`;
    }
  }

  const busContext = buildBusContextText(state.contextBlocks, state.nodeOutputs);

  // 4A.5: if incoming edges mapped data to this node, prefer it. A `content`/
  // `userContent` mapping overrides the user message; other mapped keys are
  // injected as a focused MAPPED INPUTS block. Falls back to the legacy bus when
  // no mapping exists (existing graphs unchanged).
  const mapped = state.nodeInputs?.[node.id];
  const mappedUserContent =
    mapped && (typeof mapped.content === "string" || typeof mapped.userContent === "string")
      ? String(mapped.content ?? mapped.userContent)
      : undefined;
  const mappedBlock =
    mapped && Object.keys(mapped).length
      ? `## MAPPED INPUTS\n${JSON.stringify(mapped, null, 2)}`
      : "";

  const userContent =
    mappedUserContent ??
    options?.userContent ??
    String(state.variables.body ?? state.variables.content ?? state.variables.transcript ?? "");

  const { systemPrompt: agentPrompt } = await buildContextAwareSystemPromptCore({
    threadId: null,
    lastUserMessage: userContent.slice(0, 500),
    selectedSpaceIds: [],
    selectedTopicId: null,
    selectedDocumentIds: [],
    usePersonalization: false,
    hasPersonalizationData: false,
    sourceOfTruthEnabled: false,
    agentId,
    selectedSuperPrompt: null,
    selectedWorkflow: null,
    useRAG: false,
  });

  const custom = agent.customInstructions ?? "";
  const systemParts = [
    agentPrompt,
    custom ? `## CUSTOM INSTRUCTIONS\n${custom}` : "",
    skillBlock ? `## APPLIED SKILLS\n${skillBlock}` : "",
    mappedBlock,
    busContext ? `## WORKFLOW CONTEXT\n${busContext}` : "",
    agent.outputSchema
      ? `## OUTPUT SCHEMA\nRespond with valid JSON matching:\n${JSON.stringify(agent.outputSchema, null, 2)}\nInclude confidence (0-1) and quality (0-1) scores.`
      : "Include confidence (0-1) and quality (0-1) scores when structured output is used.",
  ].filter(Boolean);

  const model = profile.preferredModel ?? "openai/gpt-4o-mini";
  const temperature = profile.temperature ?? 0.7;
  const maxTokens = profile.maxTokens ?? 4096;

  const question = userContent || "Process the workflow context above.";
  const gated = await operatingLoopService.answerWithLoop({
    userId: await getCurrentUserId(),
    question,
    pickedModel: model,
    app: options?.app ?? "workflows",
    run: async ({ model: nextModel, briefPrompt }) => {
      let text = "";
      for await (const chunk of aiService.streamChat({
        model: nextModel,
        messages: [
          { role: "system", content: [briefPrompt, systemParts.join("\n\n")].filter(Boolean).join("\n\n") },
          { role: "user", content: question },
        ],
        maxTokens,
        temperature,
        app: options?.app ?? "workflows",
      })) {
        if (chunk.type === "error") throw new Error(chunk.error || "Agent step failed");
        if (chunk.type === "delta" && chunk.content) text += chunk.content;
      }
      return { text };
    },
  });
  const content = gated.text;

  const structured = parseStructuredOutput(content);
  return {
    raw: content.trim(),
    parsed: structured.parsed,
    confidence: structured.confidence,
    quality: structured.quality,
    model,
  };
}

export async function executeCompositeAgent(
  agentId: string,
  state: WorkflowRunState,
  userContent: string,
  onSubStep?: (subAgentId: string, output: NodeOutput) => void
): Promise<NodeOutput> {
  let agent = await aiAgentsService.getAgentById(agentId);
  if (!agent) agent = AGENTS.find((a) => a.id === agentId) as typeof agent;
  const childIds = agent?.composedAgentIds ?? [];
  if (!childIds.length) {
    return executeAgentNode({ id: "composite", type: "agent", position: { x: 0, y: 0 }, data: { label: "Agent", agentId } }, state, { userContent });
  }

  const subOutputs: NodeOutput[] = [];
  let lastContent = userContent;
  for (const childId of childIds) {
    const out = await executeAgentNode(
      { id: childId, type: "agent", position: { x: 0, y: 0 }, data: { label: childId, agentId: childId } },
      state,
      { userContent: lastContent }
    );
    subOutputs.push(out);
    onSubStep?.(childId, out);
    lastContent = out.raw;
    state.nodeOutputs[`composite-${childId}`] = out;
  }

  return {
    raw: lastContent,
    parsed: { subOutputs: subOutputs.map((o) => o.parsed), merged: true },
    confidence: subOutputs[subOutputs.length - 1]?.confidence,
    quality: subOutputs[subOutputs.length - 1]?.quality,
  };
}
